import React, { useState } from "react";
import { NAV } from "./Layout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ArrowUp, ArrowDown, Eye, EyeOff, Plus, X, FolderPlus, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const LS = { order: "mwcrm_menu_order", hidden: "mwcrm_menu_hidden", groups: "mwcrm_menu_groups", assign: "mwcrm_menu_assign", font: "mwcrm_menu_fontsize" };
const read = (k, def) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } };

export default function MenuSettings() {
  const [order, setOrder] = useState(() => read(LS.order, []));
  const [hidden, setHidden] = useState(() => read(LS.hidden, []));
  const [groups, setGroups] = useState(() => read(LS.groups, []));
  const [assign, setAssign] = useState(() => read(LS.assign, {}));
  const [font, setFont] = useState(() => parseInt(localStorage.getItem(LS.font)) || 16);
  const [ng, setNg] = useState("");

  const sync = () => window.dispatchEvent(new Event("mw-menu-config"));
  const saveOrder = (v) => { setOrder(v); localStorage.setItem(LS.order, JSON.stringify(v)); sync(); };
  const saveHidden = (v) => { setHidden(v); localStorage.setItem(LS.hidden, JSON.stringify(v)); sync(); };
  const saveGroups = (v) => { setGroups(v); localStorage.setItem(LS.groups, JSON.stringify(v)); sync(); };
  const saveAssign = (v) => { setAssign(v); localStorage.setItem(LS.assign, JSON.stringify(v)); sync(); };
  const saveFont = (v) => { setFont(v); localStorage.setItem(LS.font, String(v)); sync(); };

  const orderIdx = (to) => { const i = order.indexOf(to); return i === -1 ? 999 : i; };
  const items = NAV.map((n, i) => ({ n, i })).sort((a, b) => (orderIdx(a.n.to) - orderIdx(b.n.to)) || (a.i - b.i)).map((x) => x.n);
  const move = (idx, dir) => { const arr = items.map((n) => n.to); const j = idx + dir; if (j < 0 || j >= arr.length) return; [arr[idx], arr[j]] = [arr[j], arr[idx]]; saveOrder(arr); };
  const toggleHide = (to) => saveHidden(hidden.includes(to) ? hidden.filter((x) => x !== to) : [...hidden, to]);
  const addGroup = () => { const l = ng.trim().toLocaleUpperCase("tr-TR"); if (!l) return; saveGroups([...groups, { id: "g" + Date.now(), label: l }]); setNg(""); };
  const delGroup = (id) => { saveGroups(groups.filter((g) => g.id !== id)); const a = { ...assign }; Object.keys(a).forEach((k) => { if (a[k] === id) delete a[k]; }); saveAssign(a); };
  const setItemGroup = (to, gid) => { const a = { ...assign }; if (gid) a[to] = gid; else delete a[to]; saveAssign(a); };
  const reset = () => { saveOrder([]); saveHidden([]); saveGroups([]); saveAssign({}); saveFont(16); toast.success("MENÜ VARSAYILANA DÖNDÜ"); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-400">SOL MENÜYÜ DÜZENLEYİN — ÜST MENÜ (GRUP) OLUŞTURUN, SIRALAYIN, GİZLEYİN</p>
        <Button data-testid="menu-settings-reset" variant="outline" onClick={reset} className="border-slate-700 bg-slate-800 text-slate-200"><RotateCcw className="mr-1 h-4 w-4" /> VARSAYILANA DÖN</Button>
      </div>

      {/* font size */}
      <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4">
        <span className="text-sm font-semibold text-slate-200">ÜST MENÜ YAZI BÜYÜKLÜĞÜ</span>
        <input data-testid="menu-font-range" type="range" min="12" max="28" value={font} onChange={(e) => saveFont(parseInt(e.target.value))} className="flex-1 accent-blue-500" />
        <span className="w-14 text-center font-mono text-sm text-slate-100">{font}px</span>
        <span className="rounded px-2 py-1 font-extrabold uppercase" style={{ color: "#1e3a8a", fontSize: font }}>ÖRNEK</span>
      </div>

      {/* groups */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        <div className="mb-2 text-sm font-semibold text-slate-200">ÜST MENÜLER (GRUPLAR)</div>
        <div className="space-y-2">
          {groups.map((g) => (
            <div key={g.id} className="flex items-center gap-2">
              <FolderPlus className="h-4 w-4 shrink-0 text-blue-400" />
              <Input data-testid={`ms-group-name-${g.id}`} value={g.label} onChange={(e) => saveGroups(groups.map((x) => x.id === g.id ? { ...x, label: e.target.value.toLocaleUpperCase("tr-TR") } : x))} className="h-9 w-64 border-slate-700 bg-slate-800 text-slate-100" />
              <button data-testid={`ms-group-del-${g.id}`} onClick={() => delGroup(g.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-rose-400"><X className="h-4 w-4" /></button>
            </div>
          ))}
          {groups.length === 0 && <p className="text-xs text-slate-500">HENÜZ ÜST MENÜ YOK. AŞAĞIDAN EKLEYİN (ÖRN. CARİ İŞLEMLER, OPERASYON).</p>}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Input data-testid="ms-group-new" value={ng} onChange={(e) => setNg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGroup()} placeholder="YENİ ÜST MENÜ ADI" className="h-9 w-64 border-slate-700 bg-slate-800 text-slate-100" />
          <Button data-testid="ms-group-add" onClick={addGroup} className="bg-blue-600 text-white hover:bg-blue-700"><Plus className="mr-1 h-4 w-4" /> EKLE</Button>
        </div>
      </div>

      {/* items */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        <div className="mb-2 text-sm font-semibold text-slate-200">MENÜ ÖĞELERİ — SIRALA, ÜST MENÜYE ATA, GİZLE</div>
        <div className="space-y-1.5">
          {items.map((n, idx) => {
            const tid = n.to === "/" ? "dashboard" : n.to.slice(1);
            const isHidden = hidden.includes(n.to);
            const Icon = n.icon;
            return (
              <div key={n.to} data-testid={`ms-item-${tid}`} className={`flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/40 px-2 py-1.5 ${isHidden ? "opacity-40" : ""}`}>
                <div className="flex flex-col">
                  <button data-testid={`ms-up-${tid}`} onClick={() => move(idx, -1)} className="rounded p-0.5 text-slate-400 hover:bg-slate-700"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button data-testid={`ms-down-${tid}`} onClick={() => move(idx, 1)} className="rounded p-0.5 text-slate-400 hover:bg-slate-700"><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <Icon className="h-4 w-4 shrink-0 text-slate-300" />
                <span className="flex-1 truncate text-sm font-semibold text-slate-200">{n.label}</span>
                <select data-testid={`ms-group-sel-${tid}`} value={assign[n.to] || ""} onChange={(e) => setItemGroup(n.to, e.target.value)} className="h-8 w-44 rounded border border-slate-700 bg-slate-800 text-xs text-slate-100">
                  <option value="">— ÜST MENÜ YOK —</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                </select>
                <button data-testid={`ms-hide-${tid}`} onClick={() => toggleHide(n.to)} title={isHidden ? "GÖSTER" : "GİZLE"} className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-slate-100">
                  {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
